// Compiles the engineer lane's recent transcript into a single user message for the instructor.
// Preserves order: thinking/reasoning, assistant text, tool calls, and tool results.
//
// The structured body is wrapped in a markdown fenced block (language `tandem-handoff`) so
// user-message bubbles still run through `parseMarkdown` without the browser stripping
// angle-bracket tags as bogus HTML.

/** Max characters per tool-call argument string in the handoff (e.g. write-file `content`). */
const TOOL_CALL_ARG_HANDOFF_MAX_LEN = 40;

/** Max characters for a root-level string field on tool results in the instructor handoff. */
const TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN = 800;

/** Final safety clip after formatting tool result JSON for the handoff. */
const TOOL_RESULT_HANDOFF_CLIP_MAX_LEN = 8000;

/**
 * Recursively truncate every string primitive so huge payloads (file bodies) do not
 * flood the instructor message. Keys and argument count are preserved.
 * @param {unknown} value
 * @param {number} maxLen
 * @returns {unknown}
 */
function truncateToolCallArgStrings(value, maxLen = TOOL_CALL_ARG_HANDOFF_MAX_LEN) {
  if (value == null) {
    return value;
  }
  if (typeof value === "string") {
    if (value.length <= maxLen) {
      return value;
    }
    return `${value.slice(0, maxLen)}…`;
  }
  if (Array.isArray(value)) {
    return value.map((item) => truncateToolCallArgStrings(item, maxLen));
  }
  if (typeof value === "object" && value.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = truncateToolCallArgStrings(v, maxLen);
    }
    return out;
  }
  return value;
}

/**
 * @param {string|Record<string, unknown>|null|undefined} rawArgs
 * @returns {string}
 */
function formatToolCallArgumentsForHandoff(rawArgs) {
  if (rawArgs == null) {
    return "";
  }
  if (typeof rawArgs === "string") {
    const t = rawArgs.trim();
    if (!t) {
      return "";
    }
    try {
      const parsed = JSON.parse(t);
      const truncated = truncateToolCallArgStrings(parsed);
      return JSON.stringify(truncated);
    } catch {
      return t.length <= TOOL_CALL_ARG_HANDOFF_MAX_LEN
        ? t
        : `${t.slice(0, TOOL_CALL_ARG_HANDOFF_MAX_LEN)}…`;
    }
  }
  if (typeof rawArgs === "object") {
    const truncated = truncateToolCallArgStrings(rawArgs);
    return JSON.stringify(truncated);
  }
  return String(rawArgs);
}

/**
 * True for plain objects created from JSON (not arrays, not null).
 * @param {unknown} v
 */
function _isPlainObject(v) {
  return (
    v != null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}

/**
 * Instructor handoff: keep only root-level shape. Primitives stay; each root object value
 * becomes `{}`; each root array becomes `{ _handoff: "array", length }` so huge trees
 * (e.g. get-project-overview) do not flood the message.
 * @param {unknown} parsed — JSON.parse output
 * @returns {unknown}
 */
function summarizeRootToolResultForHandoff(parsed) {
  if (parsed == null) {
    return parsed;
  }
  const t = typeof parsed;
  if (t === "string") {
    if (parsed.length <= TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN) {
      return parsed;
    }
    return `${parsed.slice(0, TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN)}…`;
  }
  if (t === "number" || t === "boolean") {
    return parsed;
  }
  if (t === "bigint") {
    return String(parsed);
  }
  if (Array.isArray(parsed)) {
    return { _handoff: "array", length: parsed.length };
  }
  if (!_isPlainObject(parsed)) {
    return "[Object]";
  }

  const out = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (v == null) {
      out[k] = v;
      continue;
    }
    const vt = typeof v;
    if (vt === "string") {
      out[k] =
        v.length <= TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN
          ? v
          : `${v.slice(0, TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN)}…`;
      continue;
    }
    if (vt === "number" || vt === "boolean") {
      out[k] = v;
      continue;
    }
    if (vt === "bigint") {
      out[k] = String(v);
      continue;
    }
    if (Array.isArray(v)) {
      out[k] = { _handoff: "array", length: v.length };
      continue;
    }
    if (_isPlainObject(v)) {
      out[k] = {};
      continue;
    }
    out[k] = "[Object]";
  }
  return out;
}

/**
 * @param {string} raw — tool message segment text (usually JSON)
 * @returns {string}
 */
function formatToolResultForHandoff(raw) {
  if (raw == null || raw === "") {
    return "(empty)";
  }
  const s = String(raw).trim();
  if (!s) {
    return "(empty)";
  }
  try {
    const parsed = JSON.parse(s);
    const summarized = summarizeRootToolResultForHandoff(parsed);
    return JSON.stringify(summarized, null, 2);
  } catch {
    return s.length <= TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN
      ? s
      : `${s.slice(0, TOOL_RESULT_ROOT_STRING_HANDOFF_MAX_LEN)}…`;
  }
}

/** @param {string} inner */
function _wrapTandemHandoffFence(inner) {
  const body = String(inner || "").replace(/\r\n/g, "\n");
  // Use a 4+ backtick fence so typical ``` code inside engineer text does not break the block.
  let fence = "````";
  while (body.includes(fence)) {
    fence += "`";
  }
  return `${fence}tandem-handoff\n${body}\n${fence}`;
}

/**
 * @param {import("../../../../systems/core/chat/Chat.js").ChatMessage} msg
 * @param {string[]} parts
 */
function _appendAssistantFromOpenRouterFallback(msg, parts) {
  if (!msg?.toOpenRouterFormat || msg.data?.role !== "assistant") {
    return;
  }
  const f = msg.toOpenRouterFormat();
  const reasoning =
    typeof f.reasoning === "string" && f.reasoning.trim() ? f.reasoning.trim() : "";
  if (reasoning) {
    parts.push(`<Thinking>\n${reasoning}\n</Thinking>`);
  }

  let text = "";
  if (typeof f.content === "string") {
    text = f.content.trim();
  } else if (Array.isArray(f.content)) {
    text = f.content
      .map((item) => {
        if (item && typeof item === "object" && item.type === "text" && typeof item.text === "string") {
          return item.text;
        }
        return "";
      })
      .join("")
      .trim();
  }
  if (text) {
    parts.push(`<Response>\n${text}\n</Response>`);
  }

  const toolCalls = Array.isArray(f.tool_calls) ? f.tool_calls : [];
  for (const tc of toolCalls) {
    const id = tc?.id || "unknown";
    const name = tc?.function?.name || "tool";
    const rawArgs = tc?.function?.arguments;
    const argsInner = formatToolCallArgumentsForHandoff(rawArgs);
    parts.push(`<Tool Call>\nid:${id}\n${name}(${argsInner})\n</Tool Call>`);
  }
}

/**
 * @param {import("../../../../systems/core/chat/Chat.js").Chat} engineerChat
 * @returns {string}
 */
export function compileEngineerTurnForInstructor(engineerChat) {
  const prefix = "[Engineer]";

  if (!engineerChat?.getAllMessages) {
    return `${prefix}\n\n(No engineer transcript.)`;
  }

  const messages = engineerChat.getAllMessages();
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.data?.role === "user") {
      lastUserIdx = i;
      break;
    }
  }

  const slice =
    lastUserIdx >= 0 ? messages.slice(lastUserIdx + 1) : messages.slice(0);

  const parts = [];

  for (const msg of slice) {
    const role = msg?.data?.role;
    if (role === "assistant") {
      for (const seg of msg.data?.segments || []) {
        if (seg.type === "reasoning" || seg.type === "thinking") {
          const t =
            typeof seg.content === "string"
              ? seg.content.trim()
              : seg.content != null
                ? String(seg.content).trim()
                : "";
          if (t) {
            parts.push(`<Thinking>\n${t}\n</Thinking>`);
          }
          continue;
        }
        if (seg.type === "text" && typeof seg.content === "string" && seg.content.trim()) {
          parts.push(`<Response>\n${seg.content.trim()}\n</Response>`);
          continue;
        }
        if (seg.type === "tool_call") {
          const id = seg.toolCallId || seg.tool_call_id || "unknown";
          const name = seg.functionName || seg.function?.name || "tool";
          const argsInner = formatToolCallArgumentsForHandoff(seg.arguments);
          parts.push(
            `<Tool Call>\nid:${id}\n${name}(${argsInner})\n</Tool Call>`
          );
        }
      }
    } else if (role === "tool") {
      let toolId = "";
      let content = "";
      for (const seg of msg.data?.segments || []) {
        if (seg.type === "function_call" && seg.content?.tool_call_id) {
          toolId = seg.content.tool_call_id;
        }
        if (seg.type === "text" && typeof seg.content === "string" && seg.content.trim()) {
          content = seg.content.trim();
        }
      }
      const body = formatToolResultForHandoff(content);
      const clipped =
        body.length > TOOL_RESULT_HANDOFF_CLIP_MAX_LEN
          ? `${body.slice(0, TOOL_RESULT_HANDOFF_CLIP_MAX_LEN)}\n… (truncated)`
          : body;
      parts.push(`<Tool Result>\nid:${toolId || "unknown"}\n${clipped}\n</Tool Result>`);
    }
  }

  if (parts.length === 0) {
    for (const msg of slice) {
      if (msg?.data?.role === "assistant") {
        _appendAssistantFromOpenRouterFallback(msg, parts);
      } else if (msg?.data?.role === "tool") {
        let toolId = "";
        let content = "";
        for (const seg of msg.data?.segments || []) {
          if (seg.type === "function_call" && seg.content?.tool_call_id) {
            toolId = seg.content.tool_call_id;
          }
          if (seg.type === "text" && typeof seg.content === "string" && seg.content.trim()) {
            content = seg.content.trim();
          }
        }
        const body = formatToolResultForHandoff(content);
        const clipped =
          body.length > TOOL_RESULT_HANDOFF_CLIP_MAX_LEN
            ? `${body.slice(0, TOOL_RESULT_HANDOFF_CLIP_MAX_LEN)}\n… (truncated)`
            : body;
        parts.push(`<Tool Result>\nid:${toolId || "unknown"}\n${clipped}\n</Tool Result>`);
      }
    }
  }

  if (parts.length === 0) {
    return `${prefix}\n\n(No visible engineer output since the last user message.)`;
  }

  const inner = parts.join("\n\n");
  return `${prefix}\n\n${_wrapTandemHandoffFence(inner)}`;
}
